<template>
  <div class="wcd-finder" :class="{ 'wcd-light': !isDarkMode }">
    <div class="wcd-scroll">
      <div v-if="!hideTitle" class="wcd-heading">
        <div class="wcd-heading-icon"><v-icon size="19" color="white">mdi-calendar-search</v-icon></div>
        <div>
          <div class="wcd-heading-title">Freien Termin finden</div>
          <div class="wcd-heading-subtitle">Verfügbarkeit, Termine und Aufgaben auf einen Blick</div>
        </div>
      </div>

      <section class="wcd-controls">
        <div class="wcd-control-row wcd-control-row-main">
          <div class="wcd-field wcd-field-employee">
            <label>Mitarbeiter</label>
            <div class="wcd-employee-filter-line">
              <v-select
                v-model="finderColId"
                :items="employeeOptions"
                item-text="name"
                item-value="id"
                dense
                outlined
                hide-details
                :dark="isDarkMode"
                class="wcd-input wcd-employee-select" />
              <button
                v-if="ownEmployee"
                type="button"
                class="wcd-own-employee-switch"
                :class="{ active: showingOwnEmployee }"
                :disabled="!ownEmployeeWithShift"
                :title="
                  ownEmployeeWithShift
                    ? showingOwnEmployee
                      ? 'Wieder alle Mitarbeitenden mit Schicht anzeigen'
                      : `Nur ${ownEmployee.name} anzeigen`
                    : `${ownEmployee.name} hat im gewählten Suchbereich keine Schicht`
                "
                @click="toggleOwnEmployeeView">
                <v-icon size="16" color="inherit">
                  {{ showingOwnEmployee ? "mdi-account-group-outline" : "mdi-account-outline" }}
                </v-icon>
                <span>{{ showingOwnEmployee ? "Alle mit Schicht" : ownEmployeeQuickLabel }}</span>
              </button>
            </div>
          </div>

          <div class="wcd-field wcd-field-date">
            <label>Suche ab</label>
            <v-menu v-model="finderDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button type="button" class="wcd-date-button" v-bind="attrs" v-on="on">
                  <v-icon size="17">mdi-calendar-blank-outline</v-icon>
                  <span>{{ formatDateFriendly(finderStartDate) }}</span>
                  <v-icon size="16" class="ml-auto">mdi-chevron-down</v-icon>
                </button>
              </template>
              <v-date-picker v-model="finderStartDate" no-title locale="de" :dark="isDarkMode" @input="finderDateMenu = false" />
            </v-menu>
          </div>
        </div>

        <div class="wcd-control-row">
          <div class="wcd-field wcd-field-grow">
            <label>Zeitfenster</label>
            <div class="wcd-choice-grid wcd-time-choices">
              <button
                v-for="option in timeOfDayOptions"
                :key="option.value"
                type="button"
                class="wcd-choice"
                :class="{ active: filterTimeOfDay === option.value }"
                @click="filterTimeOfDay = option.value">
                <v-icon size="15" color="inherit">{{ option.icon }}</v-icon>
                <span>{{ option.label }}</span>
                <small>{{ option.range }}</small>
              </button>
            </div>
          </div>

          <div class="wcd-field wcd-weekdays-field">
            <label>Wochentage</label>
            <div class="wcd-weekdays">
              <button
                v-for="day in weekdayOptions"
                :key="day.value"
                type="button"
                class="wcd-weekday"
                :class="{ active: filterWeekdays.includes(day.value) }"
                @click="toggleWeekday(day.value)">
                {{ day.label }}
              </button>
            </div>
          </div>
        </div>

        <button type="button" class="wcd-task-policy" :class="{ active: finderAllowTaskOverlap }" @click="requestTaskOverlapChange">
          <span class="wcd-task-policy-icon"><v-icon size="18" color="inherit">mdi-clipboard-clock-outline</v-icon></span>
          <span class="wcd-task-policy-copy">
            <strong>Parallel trotz Aufgaben</strong>
            <small>{{ finderAllowTaskOverlap ? "Treffer mit Aufgaben werden als Konflikt markiert" : "Aufgaben blockieren freie Termine" }}</small>
          </span>
          <span class="wcd-toggle" :class="{ on: finderAllowTaskOverlap }"><span></span></span>
        </button>
      </section>

      <div class="wcd-legend" aria-label="Legende">
        <span><i class="legend-free"></i> Frei</span>
        <span><i class="legend-appointment"></i> Termin</span>
        <span><i class="legend-task"></i> Aufgabe / Ämtli</span>
        <span><i class="legend-break"></i> Pause</span>
        <span><i class="legend-system"></i> Öffnen / Schliessen</span>
        <span><i class="legend-absence"></i> Abwesend</span>
        <span><i class="legend-off"></i> Kein Dienst</span>
      </div>

      <div v-if="date && date !== finderStartDate" class="wcd-date-notice">
        <v-icon size="16" color="primary">mdi-information-outline</v-icon>
        <span
          >Der ausgewählte Treffer ersetzt das aktuelle Datum <strong>{{ formatDateFriendly(date) }}</strong
          >.</span
        >
      </div>

      <div v-if="loadError" class="wcd-error">
        <v-icon color="error" size="20">mdi-alert-circle-outline</v-icon>
        <div>
          <strong>Verfügbarkeit konnte nicht geladen werden.</strong><small>{{ loadError }}</small>
        </div>
        <v-btn small text color="primary" @click="searchFreeSlots">Erneut versuchen</v-btn>
      </div>

      <div v-if="loadingSlots" class="wcd-loading">
        <v-progress-circular indeterminate color="primary" size="30" width="3" />
        <div><strong>Freie Zeiten werden geprüft</strong><small>Termine, Aufgaben, Pausen und Abwesenheiten werden abgeglichen.</small></div>
      </div>

      <template v-else-if="!loadError">
        <div v-if="rangeDates.length && (!dayGroups.length || !totalAvailableStarts)" class="wcd-no-results">
          <v-icon size="30" color="warning">{{ dayGroups.length ? "mdi-calendar-remove-outline" : "mdi-account-clock-outline" }}</v-icon>
          <div>
            <strong>{{ dayGroups.length ? "Keine passende freie Zeit gefunden" : "Keine passenden Schichten im Zeitraum" }}</strong>
            <small>
              {{
                dayGroups.length
                  ? "Die Belegungen bleiben unten sichtbar. Passe Mitarbeiter, Wochentage oder Zeitfenster an."
                  : "Es werden nur Mitarbeitende angezeigt, die im gewählten Zeitraum tatsächlich im Dienst sind."
              }}
            </small>
          </div>
        </div>

        <section v-if="dayGroups.length" class="wcd-days">
          <div class="wcd-section-heading wcd-timeline-heading">
            <div>
              <span class="wcd-eyebrow">Direkt auswählen</span>
              <h3>{{ timelineWindowLabel }} · Freie Fläche direkt anklicken</h3>
            </div>
            <span class="wcd-range-source" :title="sourceMode === 'range' ? 'Optimierte Bereichsabfrage' : 'Kompatibilitätsmodus'">
              <v-icon size="14" color="inherit">mdi-database-check-outline</v-icon>
              {{ rangeDates.length }} Tage
            </span>
          </div>

          <div class="wcd-schedule">
            <div class="wcd-schedule-axis" aria-hidden="true">
              <div class="wcd-axis-day">Tag</div>
              <div class="wcd-axis-content">
                <div class="wcd-axis-employee">Mitarbeiter</div>
                <div class="wcd-axis-ruler">
                  <span v-for="tick in rulerTicks" :key="tick.minute" :style="{ left: tick.left + '%' }">{{ tick.label }}</span>
                </div>
              </div>
            </div>

            <article
              v-for="(group, groupIndex) in dayGroups"
              :key="group.date"
              class="wcd-day"
              :class="{ 'wcd-day--alternate': groupIndex % 2 === 1 }">
              <header class="wcd-day-header" :title="group.dayName + ', ' + formatDateFriendly(group.date)">
                <span class="wcd-day-weekday">{{ momentWeekdayShort(group.date) }}</span>
                <div class="wcd-day-date">
                  <strong>{{ momentDay(group.date) }}</strong>
                  <span>{{ momentMonth(group.date) }}</span>
                </div>
                <small :class="{ empty: !group.slotCount }">{{ group.slotCount ? group.slotCount + " Starts" : "Keine Starts" }}</small>
              </header>

              <div class="wcd-day-body">
                <div
                  v-for="timeline in group.timelines"
                  :key="String(timeline.id)"
                  class="wcd-timeline-row"
                  :class="{ 'wcd-timeline-row--off': timeline.noService && !timeline.items.length }"
                  :style="{ height: timeline.rowHeight + 'px' }">
                  <div class="wcd-employee">
                    <v-avatar size="28"><img :src="getEmployeePictureUrl(timeline.picture)" alt="" /></v-avatar>
                    <div>
                      <strong>{{ timeline.name }}</strong
                      ><span>{{ timeline.statusLabel }}</span>
                    </div>
                  </div>

                  <div class="wcd-timeline">
                    <div
                      class="wcd-track"
                      :class="{ 'wcd-track--off': timeline.noService && !timeline.items.length }"
                      :style="{
                        height: timeline.trackHeight + 'px',
                        '--quarter-grid': durationToPercent(15) + '%',
                        '--hour-grid': durationToPercent(60) + '%',
                      }">
                      <span v-if="timeline.noService && !timeline.items.length" class="wcd-track-empty">
                        <v-icon size="14">mdi-weather-night</v-icon> Kein Dienst
                      </span>

                      <button
                        v-for="free in timeline.freeIntervals"
                        :key="'free-' + free.start + '-' + free.end"
                        type="button"
                        class="wcd-free-block"
                        :class="{ short: free.tooShort }"
                        :style="intervalStyle(free.start, free.end)"
                        :disabled="free.tooShort"
                        :title="freeTitle(free)"
                        @click.stop="selectFreeFromPointer($event, group.date, timeline, free)">
                        <v-icon v-if="free.tooShort || freeWidth(free) <= 8" size="12" color="inherit">
                          {{ free.tooShort ? "mdi-timer-sand" : "mdi-check" }}
                        </v-icon>
                        <span v-if="freeWidth(free) > 8">
                          <strong>{{ free.tooShort ? "Zu kurz" : "Frei" }}</strong>
                          · {{ minutesToTimeStr(free.start) }}–{{ minutesToTimeStr(free.end) }}
                        </span>
                      </button>

                      <v-tooltip v-for="item in timeline.items" :key="item.renderKey" top :open-delay="120" transition="fade-transition">
                        <template v-slot:activator="{ on, attrs }">
                          <button
                            type="button"
                            class="wcd-event-block"
                            :class="[
                              'event-' + item.kind,
                              {
                                muted: item.blocking === false,
                                parallel: item.kind === 'task' && finderAllowTaskOverlap,
                                dense: item.renderHeight < 19,
                                micro: item.renderHeight < 13,
                              },
                            ]"
                            :style="eventStyle(item)"
                            :aria-label="
                              item.typeLabel + ': ' + item.title + ', ' + minutesToTimeStr(item.startMin) + ' bis ' + minutesToTimeStr(item.endMin)
                            "
                            v-bind="attrs"
                            v-on="on"
                            @click.stop="inspectTimelineItem(group.date, timeline, item)">
                            <v-icon v-if="item.renderHeight >= 16 && (item.widthPct < 2.2 || item.widthPct >= 9)" size="12" color="white">
                              {{ item.icon }}
                            </v-icon>
                            <span v-else-if="item.widthPct < 4.2 && item.renderHeight >= 16" class="wcd-event-short-label">
                              {{ shortEventLabel(item) }}
                            </span>
                            <span v-if="item.widthPct >= 4.2 && item.renderHeight >= 16">{{ item.title }}</span>
                            <small v-if="item.widthPct >= 15 && item.renderHeight >= 20">
                              {{ minutesToTimeStr(item.startMin) }}–{{ minutesToTimeStr(item.endMin) }}
                            </small>
                          </button>
                        </template>
                        <div class="wcd-event-tooltip-content">
                          <span>{{ item.typeLabel }}</span>
                          <strong>{{ item.title }}</strong>
                          <small>{{ minutesToTimeStr(item.startMin) }}–{{ minutesToTimeStr(item.endMin) }} · {{ timeline.name }}</small>
                        </div>
                      </v-tooltip>

                      <div
                        v-if="activeSegment && String(activeSegment.colId) === String(timeline.id) && activeSegment.date === group.date"
                        class="wcd-selection"
                        :class="{ conflict: activeSlot && activeSlot.taskConflicts.length }"
                        :style="activeSelectionStyle">
                        <span>{{ start }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <v-expand-transition>
                <aside
                  v-if="inspectedItem && inspectedItem.date === group.date"
                  ref="inspectedDetail"
                  class="wcd-detail-card"
                  role="status"
                  :style="{ '--detail-color': inspectedItem.color }">
                  <div class="wcd-detail-icon">
                    <v-icon color="white" size="19">{{ inspectedItem.icon }}</v-icon>
                  </div>
                  <div class="wcd-detail-copy">
                    <span>{{ inspectedItem.typeLabel }}</span>
                    <strong>{{ inspectedItem.title }}</strong>
                    <small>{{ inspectedItem.employeeName }} · {{ inspectedItem.timeLabel }}</small>
                  </div>
                  <div class="wcd-detail-state" :class="{ warning: inspectedItem.kind === 'task', blocked: inspectedItem.kind !== 'task' }">
                    <v-icon size="15" color="inherit">{{ inspectedItem.kind === "task" ? "mdi-clipboard-outline" : "mdi-lock-outline" }}</v-icon>
                    {{
                      inspectedItem.kind === "task"
                        ? finderAllowTaskOverlap
                          ? inspectedItem.canParallel
                            ? "Parallel möglich"
                            : "Kein freier Dienst"
                          : "Blockiert Termin"
                        : inspectedItem.blocking === false
                        ? "Nicht aktiv"
                        : "Fest blockiert"
                    }}
                  </div>
                  <v-btn
                    v-if="inspectedItem.kind === 'task' && finderAllowTaskOverlap && inspectedItem.canParallel"
                    small
                    depressed
                    color="warning"
                    class="text-none"
                    @click="selectInspectedTaskParallel">
                    Parallel einplanen
                  </v-btn>
                  <v-btn
                    v-else-if="inspectedItem.kind === 'task' && !finderAllowTaskOverlap"
                    small
                    outlined
                    rounded
                    color="warning"
                    class="wcd-detail-action text-none font-weight-bold"
                    @click="requestTaskOverlapChange">
                    <v-icon left size="15">mdi-calendar-alert</v-icon>
                    Parallel prüfen
                  </v-btn>
                  <v-btn
                    v-else-if="inspectedItem.blocking === false && inspectedItem.canSelectUnderlying"
                    small
                    depressed
                    color="success"
                    class="text-none"
                    @click="selectInspectedAvailableTime">
                    Freie Zeit hier wählen
                  </v-btn>
                  <v-btn icon small @click="inspectedItem = null"><v-icon size="18">mdi-close</v-icon></v-btn>
                </aside>
              </v-expand-transition>
            </article>
          </div>
        </section>
      </template>
    </div>

    <v-expand-transition>
      <section v-if="activeSegment" class="wcd-selection-panel">
        <button type="button" class="wcd-close-selection" @click="clearSelection"><v-icon size="18">mdi-close</v-icon></button>
        <div class="wcd-selected-person">
          <v-avatar size="38"><img :src="getEmployeePictureUrl(activeSegment.colPicture)" alt="" /></v-avatar>
          <div>
            <span>Ausgewählter Zeitraum</span>
            <strong>{{ activeSegment.colName }}</strong>
            <small
              >{{ formatDateFriendly(activeSegment.date) }} · frei {{ minutesToTimeStr(activeSegment.startMin) }}–{{
                minutesToTimeStr(activeSegment.endMin)
              }}</small
            >
          </div>
        </div>

        <div class="wcd-start-picker">
          <label>Startzeit wählen <span>15-Minuten-Raster</span></label>
          <div class="wcd-start-options">
            <button
              v-for="slot in activeSegmentSlots"
              :key="slot.time"
              type="button"
              class="wcd-start-option"
              :class="{ active: start === slot.time, conflict: slot.taskConflicts.length, optimal: slot.isOptimal }"
              @click="selectShortcutTime(slot)">
              <strong>{{ slot.time }}</strong>
              <span>{{ slot.endTime }}</span>
              <v-icon v-if="slot.taskConflicts.length" size="13" color="warning">mdi-alert</v-icon>
              <v-icon v-else-if="slot.isOptimal" size="13" color="success">mdi-star</v-icon>
            </button>
          </div>
        </div>

        <div class="wcd-selection-summary">
          <div>
            <span>Termin</span>
            <strong>{{ start || "--:--" }}–{{ calculatedEnd }}</strong>
            <small>{{ duration }} Minuten</small>
          </div>
          <div v-if="activeSlot && activeSlot.taskConflicts.length" class="wcd-active-warning">
            <v-icon size="17" color="warning">mdi-alert-outline</v-icon>
            <span
              ><strong>Parallel zu {{ activeSlot.taskConflicts.length }} Aufgabe{{ activeSlot.taskConflicts.length === 1 ? "" : "n" }}</strong
              ><small>{{ conflictTitles(activeSlot.taskConflicts) }}</small></span
            >
          </div>
          <div v-else class="wcd-active-safe">
            <v-icon size="17" color="success">mdi-check-circle-outline</v-icon>
            <span>{{ isSelectedSlotOptimal ? "Optimaler Anschluss" : "Ohne Konflikt frei" }}</span>
          </div>
          <v-btn
            :disabled="!start || saving"
            :loading="saving"
            depressed
            color="success"
            class="text-none font-weight-bold"
            @click="confirmSelection">
            <v-icon left size="17">mdi-check</v-icon> {{ confirmLabel }}
          </v-btn>
        </div>
      </section>
    </v-expand-transition>

    <v-dialog v-model="parallelConfirmDialog" max-width="480">
      <v-card :dark="isDarkMode" class="wcd-dialog-card">
        <v-card-title class="d-flex align-center flex-nowrap">
          <span class="wcd-dialog-icon warning"><v-icon color="white">mdi-clipboard-alert-outline</v-icon></span>
          <span>Parallel trotz Aufgaben?</span>
        </v-card-title>
        <v-card-text>
          Aufgaben und Ämtli werden dann <strong>nicht als freie Zeit behandelt</strong>. Treffer, die sich überschneiden, sind deutlich als Konflikt
          markiert und müssen beim Übernehmen nochmals bestätigt werden.
          <div class="wcd-dialog-locks">
            <v-icon size="16" color="success">mdi-shield-check-outline</v-icon> Termine, Pausen, Abwesenheiten und Zeiten ohne Dienst bleiben immer
            gesperrt.
          </div>
        </v-card-text>
        <v-card-actions
          ><v-spacer /><v-btn text @click="parallelConfirmDialog = false">Abbrechen</v-btn
          ><v-btn depressed color="warning" @click="enableTaskOverlap">Bewusst aktivieren</v-btn></v-card-actions
        >
      </v-card>
    </v-dialog>

    <v-dialog v-model="taskConflictDialog" max-width="500">
      <v-card :dark="isDarkMode" class="wcd-dialog-card">
        <v-card-title class="d-flex align-center flex-nowrap">
          <span class="wcd-dialog-icon warning"><v-icon color="white">mdi-alert</v-icon></span>
          <span>Aufgabenkonflikt bestätigen</span>
        </v-card-title>
        <v-card-text>
          Der Termin <strong>{{ start }}–{{ calculatedEnd }}</strong> liegt parallel zu:
          <div class="wcd-conflict-list">
            <div v-for="conflict in pendingTaskConflicts" :key="conflict.renderKey || conflict.id">
              <span :style="{ backgroundColor: conflict.color }"
                ><v-icon size="15" color="white">{{ conflict.icon }}</v-icon></span
              >
              <div>
                <strong>{{ conflict.title }}</strong
                ><small>{{ minutesToTimeStr(conflict.startMin) }}–{{ minutesToTimeStr(conflict.endMin) }}</small>
              </div>
            </div>
          </div>
          Bitte nur fortfahren, wenn die Aufgabe trotzdem erledigt bzw. anders organisiert werden kann.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="taskConflictDialog = false">Zurück</v-btn>
          <v-btn depressed color="warning" :loading="saving" :disabled="saving" @click="confirmTaskConflictSelection">Trotzdem übernehmen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment"
import api from "@/api"

export default {
  name: "WorkCalendarAppointmentFinder",
  props: {
    employees: { type: Array, required: true },
    date: { type: String, default: "" },
    start: { type: String, default: "" },
    colId: { type: [String, Number], default: "" },
    duration: { type: Number, default: 60 },
    isDarkMode: { type: Boolean, default: true },
    excludeAppointmentId: { type: [String, Number], default: null },
    hideTitle: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    confirmLabel: { type: String, default: "Termin übernehmen" },
  },
  data() {
    return {
      finderStartDate: "",
      finderDateMenu: false,
      finderAllowTaskOverlap: false,
      finderColId: "all",
      ownEmployeeId: null,
      filterTimeOfDay: "any",
      filterWeekdays: [1, 2, 3, 4, 5, 6, 0],
      loadingSlots: false,
      loadError: "",
      sourceMode: "range",
      rangeDates: [],
      rangeEmployees: [],
      dayGroups: [],
      activeSegment: null,
      selectionOrigin: null,
      inspectedItem: null,
      parallelConfirmDialog: false,
      taskConflictDialog: false,
      pendingTaskConflicts: [],
      requestVersion: 0,
      weekdayOptions: [
        { label: "Mo", value: 1 },
        { label: "Di", value: 2 },
        { label: "Mi", value: 3 },
        { label: "Do", value: 4 },
        { label: "Fr", value: 5 },
        { label: "Sa", value: 6 },
        { label: "So", value: 0 },
      ],
      timeOfDayOptions: [
        { label: "Ganzer Tag", value: "any", icon: "mdi-calendar-range", range: "06–23" },
        { label: "Vormittag", value: "morning", icon: "mdi-weather-sunset-up", range: "06–12" },
        { label: "Nachmittag", value: "afternoon", icon: "mdi-weather-sunny", range: "12–17" },
        { label: "Abend", value: "evening", icon: "mdi-weather-night", range: "17–23" },
      ],
    }
  },
  computed: {
    timelineRange() {
      if (this.filterTimeOfDay === "morning") return { start: 360, end: 720 }
      if (this.filterTimeOfDay === "afternoon") return { start: 720, end: 1020 }
      if (this.filterTimeOfDay === "evening") return { start: 1020, end: 1380 }
      return { start: 360, end: 1380 }
    },
    timelineWindowLabel() {
      return `${this.minutesToTimeStr(this.timelineRange.start)}–${this.minutesToTimeStr(this.timelineRange.end)}`
    },
    rulerTicks() {
      const span = this.timelineRange.end - this.timelineRange.start
      const step = span > 720 ? 120 : 60
      const ticks = []
      for (let minute = this.timelineRange.start; minute <= this.timelineRange.end; minute += step) {
        ticks.push({ minute, left: this.minuteToPercent(minute), label: this.minutesToTimeStr(minute) })
      }
      return ticks
    },
    calculatedEnd() {
      if (!this.start) return "--:--"
      return this.minutesToTimeStr(this.timeToMinutes(this.start) + this.safeDuration)
    },
    safeDuration() {
      const value = Number(this.duration)
      return Number.isFinite(value) && value > 0 ? value : 30
    },
    effectiveEmployees() {
      const map = new Map()
      this.rangeEmployees.forEach((employee) => map.set(String(employee.id), employee))
      ;(this.employees || []).forEach((employee) => {
        const id = this.employeeId(employee)
        if (!id || id === "ma_info" || id === "no_show" || employee.isHelper) return
        if (!map.has(String(id))) {
          map.set(String(id), {
            id,
            employeeId: employee.employeeId || (employee.info && employee.info.employeeId),
            name: this.employeeName(employee),
            firstName: employee.firstName || (employee.info && employee.info.firstName) || this.employeeName(employee),
            picture: employee.picture || (employee.info && employee.info.picture),
            workCalendarSort:
              employee.workCalendarSort !== undefined && employee.workCalendarSort !== null
                ? employee.workCalendarSort
                : employee.info && employee.info.workCalendarSort !== undefined && employee.info.workCalendarSort !== null
                ? employee.info.workCalendarSort
                : 9999,
            days: {},
          })
        }
      })
      return Array.from(map.values()).sort((a, b) => {
        const sortA = a.workCalendarSort ?? 9999
        const sortB = b.workCalendarSort ?? 9999
        return sortA - sortB || a.name.localeCompare(b.name)
      })
    },
    employeeOptions() {
      return [{ id: "all", name: "Alle Mitarbeitenden mit Schicht" }].concat(
        this.shiftEligibleEmployees.map((employee) => ({ id: employee.id, name: employee.name })),
      )
    },
    shiftEligibleEmployees() {
      if (!this.rangeDates.length) return this.effectiveEmployees
      return this.effectiveEmployees.filter((employee) =>
        this.rangeDates.some((date) => this.filterWeekdays.includes(moment(date).day()) && this.employeeHasScheduledWork(employee, date, true)),
      )
    },
    ownEmployee() {
      if (this.ownEmployeeId === null || this.ownEmployeeId === undefined || this.ownEmployeeId === "") return null
      return this.effectiveEmployees.find((employee) => String(employee.id) === String(this.ownEmployeeId)) || null
    },
    ownEmployeeWithShift() {
      if (!this.ownEmployeeId) return null
      return this.shiftEligibleEmployees.find((employee) => String(employee.id) === String(this.ownEmployeeId)) || null
    },
    showingOwnEmployee() {
      return Boolean(this.ownEmployeeId && String(this.finderColId) === String(this.ownEmployeeId))
    },
    ownEmployeeQuickLabel() {
      const employee = this.ownEmployee
      if (!employee) return "Nur eigener MA"
      const firstName =
        employee.firstName ||
        String(employee.name || "")
          .trim()
          .split(/\s+/)[0]
      return firstName ? `Nur ${firstName}` : "Nur eigener MA"
    },
    totalAvailableStarts() {
      return this.dayGroups.reduce((total, group) => total + Number(group.slotCount || 0), 0)
    },
    activeSegmentSlots() {
      if (!this.activeSegment) return []
      const slots = []
      const latestStart = this.snapDown(this.activeSegment.endMin - this.safeDuration)
      for (let minute = this.snapUp(this.activeSegment.startMin); minute <= latestStart; minute += 15) {
        const conflicts = this.taskConflictsFor(this.activeSegment.employee, this.activeSegment.date, minute, minute + this.safeDuration)
        if (!this.finderAllowTaskOverlap && conflicts.length) continue
        slots.push({
          time: this.minutesToTimeStr(minute),
          endTime: this.minutesToTimeStr(minute + this.safeDuration),
          startMin: minute,
          taskConflicts: conflicts,
          isOptimal: minute === this.activeSegment.startMin || minute + this.safeDuration === this.activeSegment.endMin,
        })
      }
      return slots
    },
    activeSlot() {
      if (!this.start || !this.activeSegment) return null
      return this.activeSegmentSlots.find((slot) => slot.time === this.start) || null
    },
    isSelectedSlotOptimal() {
      return Boolean(this.activeSlot && this.activeSlot.isOptimal)
    },
    activeSelectionStyle() {
      if (!this.start || !this.activeSegment) return { display: "none" }
      return {
        left: `${this.minuteToPercent(this.timeToMinutes(this.start))}%`,
        width: `${this.durationToPercent(this.safeDuration)}%`,
      }
    },
  },
  watch: {
    date: {
      immediate: true,
      handler(value) {
        if (value && !this.isInternalDateChange && value !== this.finderStartDate) this.finderStartDate = value
      },
    },
    colId: {
      immediate: true,
      handler(value) {
        if (this.isInternalDateChange) return
        const reserved = ["ma_info", "no_show", "unassigned", "none"]
        const wasShowingOwnEmployee = this.showingOwnEmployee
        const nextOwnEmployeeId = value !== null && value !== undefined && value !== "" && !reserved.includes(String(value)) ? value : null
        this.ownEmployeeId = nextOwnEmployeeId
        if (wasShowingOwnEmployee) this.finderColId = nextOwnEmployeeId || "all"
      },
    },
    finderStartDate(value) {
      if (!value || this.isInternalDateChange) return
      this.clearSelection()
      this.searchFreeSlots()
    },
    finderColId() {
      this.clearSelection()
      this.rebuildFinderResults()
    },
    filterTimeOfDay() {
      this.clearSelection()
      this.ensureFinderEmployeeSelection()
      this.rebuildFinderResults()
    },
    finderAllowTaskOverlap() {
      this.clearSelection()
      this.rebuildFinderResults()
    },
    duration() {
      this.clearSelection()
      this.rebuildFinderResults()
    },
    employees: {
      deep: true,
      handler() {
        this.clearSelection()
        this.rebuildFinderResults()
      },
    },
  },
  created() {
    this.rangeCache = Object.create(null)
    this.dailyViewCache = Object.create(null)
    this.isInternalDateChange = false
  },
  mounted() {
    if (this.finderStartDate && !this.rangeDates.length && !this.loadingSlots) this.searchFreeSlots()
  },
  methods: {
    async searchFreeSlots(forceRefresh = false) {
      if (!this.finderStartDate) return
      const version = ++this.requestVersion
      const startDate = this.finderStartDate
      const endDate = moment(startDate).add(13, "days").format("YYYY-MM-DD")
      const cacheKey = `${startDate}:${endDate}:${this.excludeAppointmentId || ""}`
      if (forceRefresh === true) delete this.rangeCache[cacheKey]
      this.loadingSlots = true
      this.loadError = ""

      try {
        let normalized = this.rangeCache[cacheKey]
        if (!normalized) {
          normalized = await this.fetchRangeData(startDate, endDate)
          this.rangeCache[cacheKey] = normalized
        }
        if (version !== this.requestVersion) return
        this.rangeDates = normalized.dates
        this.rangeEmployees = normalized.employees
        this.sourceMode = normalized.sourceMode
        this.ensureFinderEmployeeSelection()
        this.rebuildFinderResults()
      } catch (error) {
        if (version !== this.requestVersion) return
        console.error("Fehler beim Laden des Termin-Finders:", error)
        this.rangeDates = []
        this.rangeEmployees = []
        this.dayGroups = []
        this.loadError = "Bitte prüfe die Verbindung und versuche es erneut."
      } finally {
        if (version === this.requestVersion) this.loadingSlots = false
      }
    },
    refreshResults() {
      this.clearSelection()
      return this.searchFreeSlots(true)
    },
    async fetchRangeData(startDate, endDate) {
      if (api.shiftManagement && typeof api.shiftManagement.fetchAppointmentFinderRange === "function") {
        try {
          const response = await api.shiftManagement.fetchAppointmentFinderRange(startDate, endDate, this.excludeAppointmentId || undefined)
          const payload = response && response.data ? response.data : response
          if (payload && Array.isArray(payload.employees)) return this.normalizeRangePayload(payload, startDate, endDate)
          throw new Error("Ungültige Antwort der Bereichsabfrage")
        } catch (error) {
          const status = error && error.response && error.response.status
          if (![404, 405, 501].includes(status)) throw error
        }
      }
      return this.fetchLegacyRange(startDate, endDate)
    },
    normalizeRangePayload(payload, startDate, endDate) {
      const dates = Array.isArray(payload.dates) && payload.dates.length ? payload.dates : this.dateRange(startDate, endDate)
      const employees = payload.employees.map((employee) => ({
        id: employee.id || employee._id,
        employeeId: employee.employeeId,
        name: employee.name || employee.fullName || employee.firstName || "Mitarbeiter",
        firstName: employee.firstName || employee.name || "Mitarbeiter",
        picture: employee.picture || null,
        workCalendarSort: employee.workCalendarSort === undefined ? 9999 : employee.workCalendarSort,
        days: employee.days || {},
      }))
      return { dates, employees, sourceMode: "range" }
    },
    async fetchLegacyRange(startDate, endDate) {
      const dates = this.dateRange(startDate, endDate)
      const views = await Promise.all(
        dates.map((date) => {
          if (!this.dailyViewCache[date]) {
            this.dailyViewCache[date] = api.shiftManagement.fetchDailyView(date).catch((error) => {
              delete this.dailyViewCache[date]
              throw error
            })
          }
          return this.dailyViewCache[date]
        }),
      )
      const employeeMap = new Map()
      views.forEach((response, index) => {
        const date = dates[index]
        const columns = (response && response.data && response.data.columns) || []
        columns.forEach((column) => {
          const id = this.employeeId(column)
          if (!id || id === "ma_info" || id === "no_show" || column.isHelper) return
          if (!employeeMap.has(String(id))) {
            employeeMap.set(String(id), {
              id,
              employeeId: column.info && column.info.employeeId,
              name: this.employeeName(column),
              firstName: column.firstName || (column.info && column.info.firstName) || this.employeeName(column),
              picture: column.picture || (column.info && column.info.picture),
              workCalendarSort: (column.info && column.info.workCalendarSort) || 9999,
              days: {},
            })
          }
          employeeMap.get(String(id)).days[date] = this.normalizeLegacyDay(column)
        })
      })
      return { dates, employees: Array.from(employeeMap.values()), sourceMode: "legacy" }
    },
    normalizeLegacyDay(column) {
      const shifts = column.shifts || []
      const events = []
      ;(column.appointments || []).forEach((appointment) => {
        events.push({
          id: appointment._id || appointment.id,
          kind: "appointment",
          title: appointment.title || "Termin",
          start: appointment.start,
          end: appointment.end,
          color: appointment.color || "#1e88e5",
          icon: appointment.icon || "mdi-calendar-clock",
          blocking: !["cancelled", "noshow"].includes(appointment.status),
          status: appointment.status,
        })
      })
      ;(column.tasks || []).forEach((task) => {
        events.push({
          id: task._id || task.id,
          kind: "task",
          title: task.title || "Aufgabe",
          start: task.time,
          end: this.minutesToTimeStr(this.timeToMinutes(task.time) + Number(task.duration || 30)),
          color: task.color || "#f59e0b",
          icon: task.icon || "mdi-checkbox-marked-circle-outline",
          blocking: true,
          status: task.status,
        })
      })
      const offset = this.openingClosingOffset()
      shifts.forEach((shift) => {
        const times = shift.times || []
        times.forEach((time, index) => {
          if (String(time.type || "").toLowerCase() === "pause") {
            events.push({
              id: `${shift._id || shift.id}-break-${index}`,
              kind: "break",
              title: "Pause",
              start: time.start,
              end: time.end,
              color: "#64748b",
              icon: "mdi-coffee-outline",
              blocking: true,
            })
          }
        })
        const workTimes = times
          .filter((time) => String(time.type || "").toLowerCase() !== "pause")
          .sort((a, b) => String(a.start).localeCompare(String(b.start)))
        const opening = shift.isOpeningShift !== undefined ? shift.isOpeningShift : shift.shiftTemplate && shift.shiftTemplate.isOpeningShift
        const closing = shift.isClosingShift !== undefined ? shift.isClosingShift : shift.shiftTemplate && shift.shiftTemplate.isClosingShift
        if (opening && workTimes.length) {
          events.push({
            id: `${shift._id || shift.id}-opening`,
            kind: "opening",
            title: "Centeröffnung",
            start: workTimes[0].start,
            end: this.minutesToTimeStr(this.timeToMinutes(workTimes[0].start) + offset),
            color: "#22c55e",
            icon: "mdi-door-open",
            blocking: true,
          })
        }
        if (closing && workTimes.length) {
          const end = workTimes[workTimes.length - 1].end
          events.push({
            id: `${shift._id || shift.id}-closing`,
            kind: "closing",
            title: "Centerschliessung",
            start: this.minutesToTimeStr(this.timeToMinutes(end) - offset),
            end,
            color: "#f97316",
            icon: "mdi-door-closed-lock",
            blocking: true,
          })
        }
      })
      return {
        absence: column.absenceReason
          ? {
              type: column.absenceReason,
              label: this.absenceLabel(column.absenceReason),
              fullDay: Boolean(column.isSick),
              timeRange: column.sickTimeRange || "",
            }
          : null,
        shifts,
        events,
      }
    },
    employeeHasScheduledWork(employee, date, withinVisibleWindow = false) {
      const day = (employee && employee.days && employee.days[date]) || null
      if (!day) return false
      return (day.shifts || []).some((shift) => {
        const times = (shift && shift.times && shift.times.length ? shift.times : shift && shift.shiftTemplate && shift.shiftTemplate.times) || []
        return times.some((time) => {
          if (String((time && time.type) || "").toLowerCase() === "pause") return false
          const start = this.timeToMinutes(time && time.start)
          const end = this.timeToMinutes(time && time.end)
          if (!this.validInterval(start, end)) return false
          if (!withinVisibleWindow) return true
          return end > this.timelineRange.start && start < this.timelineRange.end
        })
      })
    },
    ensureFinderEmployeeSelection() {
      if (this.finderColId === "all") return
      const selectedStillWorks = this.shiftEligibleEmployees.some((employee) => String(employee.id) === String(this.finderColId))
      if (!selectedStillWorks) this.finderColId = "all"
    },
    toggleOwnEmployeeView() {
      if (!this.ownEmployeeWithShift) return
      this.finderColId = this.showingOwnEmployee ? "all" : this.ownEmployeeId
    },
    rebuildFinderResults() {
      if (!this.rangeDates.length) {
        this.dayGroups = []
        return
      }
      const groups = []
      const selectedEmployees = this.shiftEligibleEmployees.filter(
        (employee) => this.finderColId === "all" || String(employee.id) === String(this.finderColId),
      )

      this.rangeDates.forEach((date) => {
        if (!this.filterWeekdays.includes(moment(date).day())) return
        const timelines = []
        let slotCount = 0
        selectedEmployees.forEach((employee) => {
          if (!this.employeeHasScheduledWork(employee, date, true)) return
          const analysis = this.analyseEmployeeDay(employee, date)
          const timeline = this.buildTimeline(employee, analysis)
          timelines.push(timeline)
          analysis.availableIntervals
            .map((interval) => this.clipInterval(interval))
            .filter(Boolean)
            .forEach((interval) => {
              const { firstStart, latestStart } = this.rasterStartBounds(interval)
              if (firstStart <= latestStart) slotCount += Math.floor((latestStart - firstStart) / 15) + 1
            })
        })
        if (timelines.length) groups.push({ date, dayName: moment(date).locale("de").format("dddd"), timelines, slotCount })
      })
      this.applyLocalTimelineSizing(groups)
      this.dayGroups = groups
    },
    analyseEmployeeDay(employee, date) {
      const day = (employee.days && employee.days[date]) || { shifts: [], events: [], absence: null }
      const workIntervals = []
      ;(day.shifts || []).forEach((shift) => {
        this.shiftWorkIntervals(shift).forEach((interval) => workIntervals.push(interval))
      })
      const mergedWork = this.mergeIntervals(workIntervals)
      const events = (day.events || [])
        .map((event, index) => this.normalizeEvent(event, index))
        .filter(
          (event) => event && !(this.excludeAppointmentId && event.kind === "appointment" && String(event.id) === String(this.excludeAppointmentId)),
        )
      const tasks = events.filter((event) => event.kind === "task" && event.blocking !== false)
      const hardEvents = events.filter((event) => event.kind !== "task" && event.blocking !== false)
      const partialSickIntervals = this.partialSickIntervalsForDay(day)
      const absenceType = String((day.absence && day.absence.type) || "").toLowerCase()
      const canUseShiftSickness = !day.absence || !["holiday", "freewish"].includes(absenceType)
      const effectiveAbsence =
        partialSickIntervals.length && canUseShiftSickness
          ? {
              ...(day.absence || {}),
              type: (day.absence && day.absence.type) || "sick",
              label: (day.absence && day.absence.label) || "Krank",
              fullDay: false,
              timeRanges: partialSickIntervals,
            }
          : day.absence
      const absenceEvents = this.absenceEvents(effectiveAbsence)
      const absenceBlocks = absenceEvents.filter((event) => event.blocking !== false)
      const baseAvailability = this.subtractIntervals(mergedWork, hardEvents.concat(absenceBlocks))
      const availableIntervals = this.finderAllowTaskOverlap ? baseAvailability : this.subtractIntervals(baseAvailability, tasks)
      return { day, workIntervals: mergedWork, events: events.concat(absenceEvents), tasks, baseAvailability, availableIntervals }
    },
    buildTimeline(employee, analysis) {
      const visibleAvailability = analysis.availableIntervals
        .map((interval) => this.clipInterval(interval))
        .filter(Boolean)
        .map((interval) => ({ ...interval, tooShort: !this.hasRasterStart(interval) }))
      const visibleEvents = analysis.events.map((event) => this.clipEvent(event)).filter(Boolean)
      const sortedEvents = visibleEvents.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin)
      const overlapClusters = []
      sortedEvents.forEach((event, index) => {
        let cluster = overlapClusters[overlapClusters.length - 1]
        // Direkt aneinandergrenzende Termine überlappen nicht. Erst wenn der
        // nächste Block wirklich vor dem Cluster-Ende beginnt, braucht dieser
        // Abschnitt eine zusätzliche Spur.
        if (!cluster || event.startMin >= cluster.maxEnd) {
          cluster = { events: [], maxEnd: event.endMin, laneCount: 1 }
          overlapClusters.push(cluster)
        }
        cluster.events.push(event)
        cluster.maxEnd = Math.max(cluster.maxEnd, event.endMin)
        event.renderKey = `${event.kind}-${event.id || index}-${event.startMin}-${index}`
        event.widthPct = this.durationToPercent(event.endMin - event.startMin)
      })

      overlapClusters.forEach((cluster) => {
        const laneEnds = []
        cluster.events.forEach((event) => {
          let lane = laneEnds.findIndex((end) => event.startMin >= end)
          if (lane === -1) {
            lane = laneEnds.length
            laneEnds.push(event.endMin)
          } else {
            laneEnds[lane] = event.endMin
          }
          event.lane = lane
        })
        cluster.laneCount = Math.max(1, laneEnds.length)
        cluster.events.forEach((event) => {
          event.clusterLaneCount = cluster.laneCount
        })
      })
      const hasVisibleWork = analysis.workIntervals.some(
        (interval) => interval.end > this.timelineRange.start && interval.start < this.timelineRange.end,
      )
      let statusLabel = "Im Dienst"
      if (analysis.day.absence) statusLabel = analysis.day.absence.label || this.absenceLabel(analysis.day.absence.type)
      else if (!hasVisibleWork) statusLabel = "Kein Dienst"
      const timeline = {
        id: employee.id,
        name: employee.name,
        picture: employee.picture,
        employee,
        statusLabel,
        noService: !hasVisibleWork,
        freeIntervals: visibleAvailability,
        items: visibleEvents,
        availability: analysis.availableIntervals,
        baseAvailability: analysis.baseAvailability,
        laneCount: Math.max(1, ...overlapClusters.map((cluster) => cluster.laneCount)),
        trackHeight: 38,
        rowHeight: 46,
      }
      this.applyTimelineHeight(timeline, 38)
      return timeline
    },
    applyLocalTimelineSizing(groups) {
      groups.forEach((group) => {
        ;(group.timelines || []).forEach((timeline) => {
          if (timeline.noService && !timeline.items.length) {
            this.applyTimelineHeight(timeline, 30)
            return
          }
          // Die Zeile bleibt immer gleich hoch. Zeitgleiche Einträge teilen
          // sich ausschliesslich den vorhandenen Innenraum, statt die gesamte
          // Mitarbeiterzeile (und damit den Finder) vertikal aufzublähen.
          this.applyTimelineHeight(timeline, 38)
        })
      })
    },
    applyTimelineHeight(timeline, trackHeight) {
      const laneGap = 3
      const innerHeight = trackHeight - 6
      timeline.trackHeight = trackHeight
      timeline.rowHeight = trackHeight + 8
      timeline.items.forEach((event) => {
        const localLaneCount = Math.max(1, event.clusterLaneCount || 1)
        const localLaneGap =
          localLaneCount <= 1 ? 0 : Math.min(laneGap, Math.max(0, Math.floor((innerHeight - localLaneCount * 4) / (localLaneCount - 1))))
        const availableLaneHeight = Math.floor((innerHeight - (localLaneCount - 1) * localLaneGap) / localLaneCount)
        // Einzelne Einträge nutzen die Spur voll aus. Nur in Abschnitten mit
        // echten zeitlichen Überschneidungen werden die Blöcke kompakt gestapelt.
        const renderHeight = localLaneCount === 1 ? innerHeight : Math.max(1, Math.min(24, availableLaneHeight))
        const stackHeight = localLaneCount * renderHeight + (localLaneCount - 1) * localLaneGap
        const stackTop = Math.max(3, Math.floor((trackHeight - stackHeight) / 2))
        event.renderHeight = renderHeight
        event.renderTop = stackTop + event.lane * (renderHeight + localLaneGap)
      })
    },
    normalizeEvent(event, index) {
      if (!event || !event.start || !event.end) return null
      const startMin = this.timeToMinutes(event.start)
      const endMin = this.timeToMinutes(event.end)
      if (!this.validInterval(startMin, endMin)) return null
      const kind = event.kind || event.type || "appointment"
      return {
        ...event,
        id: event.id || event._id || `${kind}-${index}`,
        kind,
        title: event.title || this.typeLabel(kind),
        startMin,
        endMin,
        color: this.safeColor(event.color, this.defaultEventColor(kind)),
        icon: event.icon || this.defaultEventIcon(kind),
        blocking: event.blocking !== false,
        typeLabel: this.typeLabel(kind),
      }
    },
    absenceEvents(absence) {
      if (!absence) return []
      const color = this.defaultEventColor("absence")
      const title = absence.label || this.absenceLabel(absence.type)
      const explicitIntervals = absence.fullDay
        ? []
        : Array.isArray(absence.timeRanges) && absence.timeRanges.length
        ? this.mergeIntervals(absence.timeRanges)
        : this.parseTimeRanges(absence.timeRange)
      const unknownPartial = !absence.fullDay && !explicitIntervals.length
      const intervals = absence.fullDay || unknownPartial ? [{ start: this.timelineRange.start, end: this.timelineRange.end }] : explicitIntervals

      return intervals.map((interval, index) => ({
        id: `absence-${absence.type || "unknown"}-${index}`,
        kind: "absence",
        title: unknownPartial ? `${title} · Zeit nicht angegeben` : title,
        startMin: interval.start,
        endMin: interval.end,
        color,
        icon: this.defaultEventIcon("absence"),
        blocking: true,
        typeLabel: "Abwesenheit",
      }))
    },
    partialSickIntervalsForDay(day) {
      const intervals = []
      ;(day.shifts || []).forEach((shift) => {
        if (!shift || !shift.isSick || Number(shift.sickPercentage ?? 100) >= 100) return
        const workIntervals = this.shiftWorkIntervals(shift)
        if (!workIntervals.length) return

        const explicitRanges = this.parseTimeRanges(shift.sickTimeRange)
        if (explicitRanges.length) {
          explicitRanges.forEach((range) => {
            workIntervals.forEach((work) => {
              const start = Math.max(range.start, work.start)
              const end = Math.min(range.end, work.end)
              if (this.validInterval(start, end)) intervals.push({ start, end })
            })
          })
          return
        }

        const totalWorkMinutes = workIntervals.reduce((sum, interval) => sum + interval.end - interval.start, 0)
        let remainingSickMinutes = Math.max(0, Math.min(totalWorkMinutes, Math.round(totalWorkMinutes * (Number(shift.sickPercentage) / 100))))
        workIntervals.forEach((work) => {
          if (remainingSickMinutes <= 0) return
          const duration = Math.min(remainingSickMinutes, work.end - work.start)
          if (duration > 0) intervals.push({ start: work.start, end: work.start + duration })
          remainingSickMinutes -= duration
        })
      })
      return this.mergeIntervals(intervals)
    },
    shiftWorkIntervals(shift) {
      const rawTimes =
        (shift && Array.isArray(shift.times) && shift.times.length
          ? shift.times
          : shift && shift.shiftTemplate && Array.isArray(shift.shiftTemplate.times)
          ? shift.shiftTemplate.times
          : []) || []
      const workIntervals = this.mergeIntervals(
        rawTimes
          .filter((time) => String((time && time.type) || "").toLowerCase() !== "pause")
          .map((time) => ({ start: this.timeToMinutes(time.start), end: this.timeToMinutes(time.end) }))
          .filter((interval) => this.validInterval(interval.start, interval.end)),
      )
      const pauseIntervals = this.mergeIntervals(
        rawTimes
          .filter((time) => String((time && time.type) || "").toLowerCase() === "pause")
          .map((time) => ({ start: this.timeToMinutes(time.start), end: this.timeToMinutes(time.end) }))
          .filter((interval) => this.validInterval(interval.start, interval.end)),
      )
      return this.subtractIntervals(workIntervals, pauseIntervals)
    },
    taskConflictsFor(employee, date, start, end) {
      const day = (employee.days && employee.days[date]) || { events: [] }
      return (day.events || [])
        .map((event, index) => this.normalizeEvent(event, index))
        .filter((event) => event && event.kind === "task" && event.blocking !== false && event.startMin < end && event.endMin > start)
    },
    subtractIntervals(baseIntervals, blockers) {
      let result = baseIntervals.map((interval) => ({ start: interval.start, end: interval.end }))
      blockers.forEach((blocker) => {
        const blockStart = blocker.startMin === undefined ? blocker.start : blocker.startMin
        const blockEnd = blocker.endMin === undefined ? blocker.end : blocker.endMin
        if (!this.validInterval(blockStart, blockEnd)) return
        const next = []
        result.forEach((interval) => {
          if (blockEnd <= interval.start || blockStart >= interval.end) next.push(interval)
          else {
            if (interval.start < blockStart) next.push({ start: interval.start, end: blockStart })
            if (interval.end > blockEnd) next.push({ start: blockEnd, end: interval.end })
          }
        })
        result = next
      })
      return result.filter((interval) => interval.end > interval.start)
    },
    mergeIntervals(intervals) {
      const sorted = intervals.slice().sort((a, b) => a.start - b.start)
      const merged = []
      sorted.forEach((interval) => {
        const last = merged[merged.length - 1]
        if (!last || interval.start > last.end) merged.push({ ...interval })
        else last.end = Math.max(last.end, interval.end)
      })
      return merged
    },
    clipInterval(interval) {
      const start = Math.max(interval.start, this.timelineRange.start)
      const end = Math.min(interval.end, this.timelineRange.end)
      return end > start ? { start, end } : null
    },
    clipEvent(event) {
      const startMin = Math.max(event.startMin, this.timelineRange.start)
      const endMin = Math.min(event.endMin, this.timelineRange.end)
      return endMin > startMin ? { ...event, startMin, endMin } : null
    },
    toggleWeekday(value) {
      if (this.filterWeekdays.includes(value)) {
        if (this.filterWeekdays.length === 1) return
        this.filterWeekdays = this.filterWeekdays.filter((day) => day !== value)
      } else this.filterWeekdays = this.filterWeekdays.concat(value)
      this.clearSelection()
      this.ensureFinderEmployeeSelection()
      this.rebuildFinderResults()
    },
    requestTaskOverlapChange() {
      if (this.finderAllowTaskOverlap) {
        this.finderAllowTaskOverlap = false
        return
      }
      this.parallelConfirmDialog = true
    },
    enableTaskOverlap() {
      this.parallelConfirmDialog = false
      this.finderAllowTaskOverlap = true
    },
    selectFreeFromPointer(event, date, timeline, interval) {
      if (interval.tooShort) return
      const { firstStart, latestStart } = this.rasterStartBounds(interval)
      if (firstStart > latestStart) return
      const rect = event.currentTarget.getBoundingClientRect()
      const ratio = rect.width ? Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) : 0
      const rawMinute = interval.start + ratio * (interval.end - interval.start)
      const minute = Math.max(firstStart, Math.min(latestStart, this.snap(rawMinute)))
      const conflicts = this.taskConflictsFor(timeline.employee, date, minute, minute + this.safeDuration)
      this.activateSlot(
        {
          date,
          time: this.minutesToTimeStr(minute),
          endTime: this.minutesToTimeStr(minute + this.safeDuration),
          startMin: minute,
          endMin: minute + this.safeDuration,
          colId: timeline.id,
          colName: timeline.name,
          colPicture: timeline.picture,
          employee: timeline.employee,
          taskConflicts: conflicts,
        },
        interval,
      )
    },
    inspectTimelineItem(date, timeline, item) {
      // Detailansicht und Zeitauswahl sind zwei unterschiedliche Modi. Beim
      // Öffnen eines belegten Blocks wird eine bestehende Auswahl sauber
      // verworfen, damit nie zwei aktive Zustände gleichzeitig erscheinen.
      if (this.activeSegment) this.clearSelection()
      const rawParallelInterval =
        item.kind === "task" ? timeline.baseAvailability.find((candidate) => candidate.start < item.endMin && candidate.end > item.startMin) : null
      const parallelInterval = rawParallelInterval ? this.clipInterval(rawParallelInterval) : null
      const underlyingInterval =
        item.blocking === false
          ? timeline.freeIntervals.find(
              (candidate) => candidate.start < item.endMin && candidate.end > item.startMin && this.hasRasterStart(candidate),
            )
          : null
      const underlyingBounds = underlyingInterval ? this.rasterStartBounds(underlyingInterval) : null
      const underlyingStart = underlyingBounds
        ? Math.max(underlyingBounds.firstStart, Math.min(underlyingBounds.latestStart, this.snap(item.startMin)))
        : null
      this.inspectedItem = {
        ...item,
        date,
        employee: timeline.employee,
        employeeName: timeline.name,
        employeePicture: timeline.picture,
        timeLabel: `${this.minutesToTimeStr(item.startMin)}–${this.minutesToTimeStr(item.endMin)}`,
        canParallel: Boolean(parallelInterval && this.hasRasterStart(parallelInterval)),
        canSelectUnderlying: Number.isFinite(underlyingStart),
        underlyingInterval,
        underlyingStart,
      }
      this.$nextTick(() => {
        const detailRef = this.$refs.inspectedDetail
        const detailElement = Array.isArray(detailRef) ? detailRef[0] : detailRef
        if (detailElement && typeof detailElement.scrollIntoView === "function") {
          try {
            detailElement.scrollIntoView({ behavior: "smooth", block: "nearest" })
          } catch {
            detailElement.scrollIntoView(false)
          }
        }
      })
    },
    selectInspectedTaskParallel() {
      if (!this.inspectedItem || this.inspectedItem.kind !== "task") return
      const item = this.inspectedItem
      const timeline = this.findTimeline(item.date, item.employee.id)
      if (!timeline) return
      const rawInterval = timeline.baseAvailability.find((candidate) => candidate.start < item.endMin && candidate.end > item.startMin)
      const interval = rawInterval ? this.clipInterval(rawInterval) : null
      if (!interval || !this.hasRasterStart(interval)) return
      const { firstStart, latestStart } = this.rasterStartBounds(interval)
      const minute = Math.max(firstStart, Math.min(latestStart, this.snap(item.startMin)))
      this.activateSlot(
        {
          date: item.date,
          time: this.minutesToTimeStr(minute),
          endTime: this.minutesToTimeStr(minute + this.safeDuration),
          startMin: minute,
          endMin: minute + this.safeDuration,
          colId: item.employee.id,
          colName: item.employee.name,
          colPicture: item.employee.picture,
          employee: item.employee,
          taskConflicts: this.taskConflictsFor(item.employee, item.date, minute, minute + this.safeDuration),
        },
        interval,
      )
      this.inspectedItem = null
    },
    selectInspectedAvailableTime() {
      if (!this.inspectedItem || !this.inspectedItem.canSelectUnderlying) return
      const item = this.inspectedItem
      const timeline = this.findTimeline(item.date, item.employee.id)
      const interval = item.underlyingInterval
      const minute = item.underlyingStart
      if (!timeline || !interval || !Number.isFinite(minute)) return
      this.activateSlot(
        {
          date: item.date,
          time: this.minutesToTimeStr(minute),
          endTime: this.minutesToTimeStr(minute + this.safeDuration),
          startMin: minute,
          endMin: minute + this.safeDuration,
          colId: timeline.id,
          colName: timeline.name,
          colPicture: timeline.picture,
          employee: timeline.employee,
          taskConflicts: this.taskConflictsFor(timeline.employee, item.date, minute, minute + this.safeDuration),
        },
        interval,
      )
    },
    activateSlot(slot, interval) {
      this.inspectedItem = null
      if (!this.selectionOrigin) {
        this.selectionOrigin = {
          date: this.date,
          start: this.start,
          colId: this.colId,
        }
      }
      this.activeSegment = {
        date: slot.date,
        colId: slot.colId,
        colName: slot.colName,
        colPicture: slot.colPicture,
        employee: slot.employee,
        startMin: interval.start,
        endMin: interval.end,
      }
      this.emitSlotSelection(slot.time, slot.taskConflicts || [])
    },
    selectShortcutTime(slot) {
      this.emitSlotSelection(slot.time, slot.taskConflicts || [])
    },
    emitSlotSelection(time, conflicts) {
      if (!this.activeSegment) return
      this.isInternalDateChange = true
      this.$emit("update:date", this.activeSegment.date)
      this.$emit("update:start", time)
      this.$emit("update:col-id", this.activeSegment.colId)
      this.$emit("select-slot", this.buildSelectionPayload(time, conflicts))
      this.$nextTick(() => {
        this.isInternalDateChange = false
      })
    },
    buildSelectionPayload(time, conflicts) {
      const startMin = this.timeToMinutes(time)
      return {
        date: this.activeSegment.date,
        start: time,
        end: this.minutesToTimeStr(startMin + this.safeDuration),
        duration: this.safeDuration,
        colId: this.activeSegment.colId,
        colName: this.activeSegment.colName,
        colPicture: this.activeSegment.colPicture,
        allowTaskOverlap: this.finderAllowTaskOverlap,
        taskConflicts: (conflicts || []).map((conflict) => ({
          id: conflict.id,
          title: conflict.title,
          start: this.minutesToTimeStr(conflict.startMin),
          end: this.minutesToTimeStr(conflict.endMin),
          color: conflict.color,
          icon: conflict.icon,
        })),
      }
    },
    confirmSelection() {
      if (!this.activeSlot || this.saving) return
      if (this.activeSlot.taskConflicts.length) {
        this.pendingTaskConflicts = this.activeSlot.taskConflicts
        this.taskConflictDialog = true
        return
      }
      this.$emit("select-and-close", this.buildSelectionPayload(this.start, []))
    },
    confirmTaskConflictSelection() {
      if (this.saving) return
      const payload = this.buildSelectionPayload(this.start, this.pendingTaskConflicts)
      this.taskConflictDialog = false
      this.pendingTaskConflicts = []
      this.$emit("select-and-close", payload)
    },
    clearSelection() {
      const origin = this.selectionOrigin
      this.selectionOrigin = null
      this.activeSegment = null
      this.inspectedItem = null
      this.pendingTaskConflicts = []
      this.taskConflictDialog = false
      if (origin) {
        this.isInternalDateChange = true
        this.$emit("update:date", origin.date)
        this.$emit("update:start", origin.start)
        this.$emit("update:col-id", origin.colId)
        this.$nextTick(() => {
          this.isInternalDateChange = false
        })
      }
      this.$emit("clear-selection")
    },
    findTimeline(date, employeeId) {
      const group = this.dayGroups.find((entry) => entry.date === date)
      return group && group.timelines.find((timeline) => String(timeline.id) === String(employeeId))
    },
    intervalStyle(start, end) {
      return { left: `${this.minuteToPercent(start)}%`, width: `${this.durationToPercent(end - start)}%` }
    },
    eventStyle(item) {
      return {
        left: `${this.minuteToPercent(item.startMin)}%`,
        width: `${this.durationToPercent(item.endMin - item.startMin)}%`,
        top: `${item.renderTop}px`,
        height: `${item.renderHeight}px`,
        backgroundColor: item.color,
        borderColor: item.color,
      }
    },
    minuteToPercent(minute) {
      const span = this.timelineRange.end - this.timelineRange.start
      return Math.max(0, Math.min(100, ((minute - this.timelineRange.start) / span) * 100))
    },
    durationToPercent(duration) {
      return (duration / (this.timelineRange.end - this.timelineRange.start)) * 100
    },
    freeWidth(interval) {
      return this.durationToPercent(interval.end - interval.start)
    },
    freeTitle(interval) {
      const state = interval.tooShort ? `Frei, aber kürzer als ${this.safeDuration} Minuten` : "Freie Zeit auswählen"
      return `${state} · ${this.minutesToTimeStr(interval.start)}–${this.minutesToTimeStr(interval.end)}`
    },
    dateRange(startDate, endDate) {
      const dates = []
      const cursor = moment(startDate)
      const end = moment(endDate)
      while (cursor.isSameOrBefore(end, "day")) {
        dates.push(cursor.format("YYYY-MM-DD"))
        cursor.add(1, "day")
      }
      return dates
    },
    timeToMinutes(value) {
      if (typeof value === "number") return value
      if (!value || !/^\d{1,2}:\d{2}/.test(String(value))) return NaN
      const parts = String(value).split(":").map(Number)
      return parts[0] * 60 + parts[1]
    },
    minutesToTimeStr(value) {
      if (!Number.isFinite(Number(value))) return "--:--"
      const normalized = Math.max(0, Math.min(1439, Math.round(Number(value))))
      const hours = Math.floor(normalized / 60)
      const minutes = normalized % 60
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
    },
    snap(value) {
      return Math.round(value / 15) * 15
    },
    snapUp(value) {
      return Math.ceil(value / 15) * 15
    },
    snapDown(value) {
      return Math.floor(value / 15) * 15
    },
    rasterStartBounds(interval) {
      return {
        firstStart: this.snapUp(interval.start),
        latestStart: this.snapDown(interval.end - this.safeDuration),
      }
    },
    hasRasterStart(interval) {
      if (!interval) return false
      const { firstStart, latestStart } = this.rasterStartBounds(interval)
      return firstStart <= latestStart
    },
    validInterval(start, end) {
      return Number.isFinite(start) && Number.isFinite(end) && end > start
    },
    parseTimeRanges(value) {
      const ranges = []
      const matcher = /(\d{1,2}):(\d{2})\s*(?:-|\u2013|\u2014|bis)\s*(\d{1,2}):(\d{2})/gi
      let match = null
      while ((match = matcher.exec(String(value || ""))) !== null) {
        const startHour = Number(match[1])
        const startMinute = Number(match[2])
        const endHour = Number(match[3])
        const endMinute = Number(match[4])
        if (
          startHour < 0 ||
          startHour > 23 ||
          endHour < 0 ||
          endHour > 23 ||
          startMinute < 0 ||
          startMinute > 59 ||
          endMinute < 0 ||
          endMinute > 59
        ) {
          continue
        }
        const start = startHour * 60 + startMinute
        const end = endHour * 60 + endMinute
        if (this.validInterval(start, end)) ranges.push({ start, end })
      }
      return this.mergeIntervals(ranges)
    },
    parseTimeRange(value) {
      return this.parseTimeRanges(value)[0] || null
    },
    employeeId(employee) {
      return employee.id || employee._id || (employee.info && (employee.info._id || employee.info.id))
    },
    employeeName(employee) {
      return (
        employee.name ||
        employee.fullName ||
        (employee.info && (employee.info.fullName || employee.info.name)) ||
        [employee.firstName || (employee.info && employee.info.firstName), employee.lastName || (employee.info && employee.info.lastName)]
          .filter(Boolean)
          .join(" ") ||
        "Mitarbeiter"
      )
    },
    openingClosingOffset() {
      const studio = this.$store && this.$store.state && this.$store.state.studioStore && this.$store.state.studioStore.selectedStudio
      return studio && studio.openingClosingOffset !== undefined ? Number(studio.openingClosingOffset) : 30
    },
    safeColor(value, fallback) {
      const color = String(value || "").trim()
      if (/^#[0-9a-f]{3,8}$/i.test(color) || /^rgba?\([\d\s,.%]+\)$/i.test(color) || /^hsla?\([\d\s,.%]+\)$/i.test(color) || /^[a-z]+$/i.test(color))
        return color
      return fallback
    },
    defaultEventColor(kind) {
      return (
        { appointment: "#1e88e5", task: "#f59e0b", break: "#64748b", opening: "#22c55e", closing: "#f97316", absence: "#dc2626" }[kind] || "#475569"
      )
    },
    defaultEventIcon(kind) {
      return (
        {
          appointment: "mdi-calendar-clock",
          task: "mdi-checkbox-marked-circle-outline",
          break: "mdi-coffee-outline",
          opening: "mdi-door-open",
          closing: "mdi-door-closed-lock",
          absence: "mdi-account-off-outline",
        }[kind] || "mdi-calendar"
      )
    },
    typeLabel(kind) {
      return (
        {
          appointment: "Termin",
          task: "Aufgabe / Ämtli",
          break: "Pause",
          opening: "Centeröffnung",
          closing: "Centerschliessung",
          absence: "Abwesenheit",
        }[kind] || "Belegung"
      )
    },
    shortEventLabel(item) {
      const title = String((item && item.title) || (item && item.typeLabel) || "").trim()
      if (!title) return "•"
      const words = title.split(/\s+/).filter(Boolean)
      if (words.length > 1)
        return words
          .slice(0, 2)
          .map((word) => Array.from(word)[0])
          .join("")
          .toUpperCase()
      return Array.from(title).slice(0, 2).join("").toUpperCase()
    },
    absenceLabel(type) {
      return (
        { sick: "Krank", holiday: "Ferien", freewish: "Freiwunsch", accident: "Unfall", military: "Militär", school: "Schule" }[type] || "Abwesend"
      )
    },
    conflictTitles(conflicts) {
      return conflicts
        .map((conflict) => `${conflict.title} ${this.minutesToTimeStr(conflict.startMin)}–${this.minutesToTimeStr(conflict.endMin)}`)
        .join(", ")
    },
    formatDateFriendly(date) {
      return date ? moment(date).format("DD.MM.YYYY") : ""
    },
    momentDay(date) {
      return moment(date).format("DD")
    },
    momentWeekdayShort(date) {
      return moment(date).locale("de").format("dd").replace(".", "").toUpperCase()
    },
    momentMonth(date) {
      return moment(date).locale("de").format("MMM")
    },
    getEmployeePictureUrl(picture) {
      return picture ? `/img/profilbilder/${picture}` : "/img/icons/user_placeholder.png"
    },
  },
}
</script>

<style scoped>
.wcd-finder {
  --surface: #15171b;
  --surface-2: #1c1f24;
  --surface-3: #23272e;
  --border: rgba(255, 255, 255, 0.09);
  --text: #f4f7fb;
  --muted: #8e99a8;
  --free: #22a35a;
  --warning: #f59e0b;
  --day-divider: #0b0d10;
  --day-alternate: rgba(255, 255, 255, 0.024);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  color: var(--text);
  background: #111318;
  overflow: hidden;
}
.wcd-finder.wcd-light {
  --surface: #fff;
  --surface-2: #f7f8fa;
  --surface-3: #eef1f4;
  --border: rgba(17, 24, 39, 0.12);
  --text: #17202b;
  --muted: #667085;
  --day-divider: #dfe5ea;
  --day-alternate: rgba(15, 23, 42, 0.028);
  background: #fbfcfd;
}
.wcd-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 18px;
  scrollbar-width: thin;
}
.wcd-heading {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 15px;
}
.wcd-heading-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, var(--v-primary-base), #15733b);
  box-shadow: 0 8px 20px rgba(0, 150, 63, 0.2);
}
.wcd-heading-title {
  font-size: 1rem;
  font-weight: 900;
  letter-spacing: -0.01em;
}
.wcd-heading-subtitle {
  margin-top: 1px;
  font-size: 0.72rem;
  color: var(--muted);
}
.wcd-controls {
  padding: 13px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
}
.wcd-control-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}
.wcd-control-row + .wcd-control-row {
  margin-top: 12px;
}
.wcd-field {
  min-width: 0;
}
.wcd-field-grow,
.wcd-field-employee {
  flex: 1;
}
.wcd-field-date {
  width: 164px;
  flex: 0 0 164px;
}
.wcd-weekdays-field {
  width: 45%;
}
.wcd-field > label,
.wcd-start-picker > label {
  display: block;
  margin-bottom: 5px;
  color: var(--muted);
  font-size: 0.63rem;
  font-weight: 800;
  letter-spacing: 0.075em;
  text-transform: uppercase;
}
.wcd-employee-filter-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 7px;
  align-items: stretch;
}
.wcd-employee-select {
  min-width: 0;
}
.wcd-own-employee-switch {
  min-width: 116px;
  height: 42px;
  padding: 0 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--muted);
  border: 1px solid var(--border);
  border-radius: 9px;
  background: var(--surface-2);
  font-size: 0.66rem;
  font-weight: 850;
  white-space: nowrap;
  transition: color 0.16s ease, border-color 0.16s ease, background 0.16s ease, transform 0.16s ease;
}
.wcd-own-employee-switch:hover:not(:disabled) {
  color: var(--text);
  border-color: rgba(34, 163, 90, 0.4);
  transform: translateY(-1px);
}
.wcd-own-employee-switch.active {
  color: #75dc99;
  border-color: rgba(34, 163, 90, 0.55);
  background: rgba(34, 163, 90, 0.13);
}
.wcd-light .wcd-own-employee-switch.active {
  color: #187b43;
}
.wcd-own-employee-switch:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.wcd-input ::v-deep .v-input__slot {
  min-height: 42px !important;
  border-radius: 9px !important;
  font-size: 0.78rem;
}
.wcd-date-button {
  width: 100%;
  height: 42px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid var(--border);
  border-radius: 9px;
  color: var(--text);
  background: var(--surface-2);
  font-size: 0.76rem;
  font-weight: 700;
}
.wcd-date-button .v-icon {
  color: var(--muted);
}
.wcd-choice-grid {
  display: grid;
  gap: 6px;
}
.wcd-time-choices {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.wcd-choice {
  min-height: 45px;
  padding: 6px 7px;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 5px;
  align-items: center;
  text-align: left;
  border: 1px solid var(--border);
  border-radius: 9px;
  color: var(--muted);
  background: var(--surface-2);
  transition: 0.16s ease;
}
.wcd-choice span {
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
}
.wcd-choice small {
  grid-column: 2;
  font-size: 0.58rem;
  opacity: 0.75;
}
.wcd-choice.active {
  color: var(--v-primary-base);
  border-color: rgba(30, 180, 87, 0.55);
  background: rgba(30, 180, 87, 0.1);
}
.wcd-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}
.wcd-weekday {
  min-width: 34px;
  min-height: 45px;
  border: 1px solid var(--border);
  border-radius: 9px;
  color: var(--muted);
  background: var(--surface-2);
  font-size: 0.68rem;
  font-weight: 900;
}
.wcd-weekday.active {
  color: #75dc99;
  border-color: rgba(34, 163, 90, 0.52);
  background: rgba(34, 163, 90, 0.15);
}
.wcd-light .wcd-weekday.active {
  color: #187b43;
}
.wcd-task-policy {
  width: 100%;
  min-height: 52px;
  margin-top: 12px;
  padding: 7px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--border);
  border-radius: 11px;
  color: var(--muted);
  background: var(--surface-2);
  text-align: left;
}
.wcd-task-policy.active {
  color: #fbbf24;
  border-color: rgba(245, 158, 11, 0.5);
  background: rgba(245, 158, 11, 0.09);
}
.wcd-task-policy-icon {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(148, 163, 184, 0.1);
}
.wcd-task-policy-copy {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.wcd-task-policy-copy strong {
  color: var(--text);
  font-size: 0.75rem;
}
.wcd-task-policy-copy small {
  margin-top: 1px;
  font-size: 0.63rem;
  color: var(--muted);
}
.wcd-toggle {
  width: 35px;
  height: 20px;
  padding: 2px;
  flex: 0 0 35px;
  border-radius: 20px;
  background: #454b54;
  transition: 0.2s;
}
.wcd-toggle span {
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: 0.2s;
}
.wcd-toggle.on {
  background: #f59e0b;
}
.wcd-toggle.on span {
  transform: translateX(15px);
}
.wcd-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 0 2px;
  color: var(--muted);
  font-size: 0.61rem;
  font-weight: 700;
}
.wcd-legend span {
  min-height: 25px;
  padding: 3px 7px;
  display: flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--border);
  border-radius: 20px;
  background: var(--surface);
}
.wcd-legend i {
  width: 9px;
  height: 9px;
  border-radius: 3px;
  display: inline-block;
}
.legend-free {
  box-sizing: border-box;
  background: rgba(34, 163, 90, 0.12);
  border: 1px dashed #22a35a;
}
.legend-appointment {
  background: #1e88e5;
}
.legend-task {
  background: #f59e0b;
}
.legend-break {
  background: #64748b;
}
.legend-system {
  background: linear-gradient(90deg, #22c55e 0 50%, #f97316 50%);
}
.legend-absence {
  background: #dc2626;
}
.legend-off {
  background: repeating-linear-gradient(135deg, #3f4650 0 3px, #22262d 3px 6px);
}
.wcd-date-notice,
.wcd-error,
.wcd-no-results {
  margin-top: 12px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 9px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  font-size: 0.7rem;
  color: var(--muted);
}
.wcd-error {
  border-color: rgba(239, 68, 68, 0.3);
}
.wcd-error > div,
.wcd-no-results > div {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.wcd-error strong,
.wcd-no-results strong {
  color: var(--text);
}
.wcd-error small,
.wcd-no-results small {
  margin-top: 2px;
}
.wcd-loading {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 13px;
  color: var(--muted);
}
.wcd-loading div {
  display: flex;
  flex-direction: column;
}
.wcd-loading strong {
  color: var(--text);
  font-size: 0.8rem;
}
.wcd-loading small {
  margin-top: 3px;
  font-size: 0.68rem;
}
.wcd-days {
  margin-top: 12px;
}
.wcd-section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
.wcd-section-heading h3 {
  margin: 0;
  color: var(--text);
  font-size: 0.82rem;
  line-height: 1.2;
}
.wcd-eyebrow {
  display: block;
  margin-bottom: 2px;
  color: var(--v-primary-base);
  font-size: 0.56rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}
.wcd-range-source {
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--border);
  border-radius: 20px;
  color: var(--muted);
  background: var(--surface);
  font-size: 0.6rem;
  font-weight: 800;
}
.wcd-timeline-heading {
  margin-top: 4px;
}

.wcd-schedule {
  overflow: visible;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.07);
}
.wcd-schedule-axis {
  position: sticky;
  top: 0;
  z-index: 10;
  min-height: 28px;
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  color: var(--muted);
  background: var(--surface-3);
  border-bottom: 1px solid var(--border);
  border-radius: 11px 11px 0 0;
}
.wcd-axis-day,
.wcd-axis-employee {
  display: flex;
  align-items: center;
  font-size: 0.5rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.wcd-axis-day {
  justify-content: center;
  border-right: 1px solid var(--border);
}
.wcd-axis-content {
  min-width: 0;
  padding: 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.wcd-axis-employee {
  width: 108px;
  flex: 0 0 108px;
}
.wcd-axis-ruler {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 28px;
}
.wcd-axis-ruler span {
  position: absolute;
  top: 8px;
  transform: translateX(-50%);
  color: var(--muted);
  font-size: 0.47rem;
  font-weight: 800;
  letter-spacing: 0.01em;
}
.wcd-axis-ruler span:first-child {
  transform: none;
}
.wcd-axis-ruler span:last-child {
  transform: translateX(-100%);
}

.wcd-day {
  position: relative;
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  background: var(--surface);
}
.wcd-day + .wcd-day {
  border-top: 7px solid var(--day-divider);
  box-shadow: inset 0 1px 0 var(--border);
}
.wcd-day--alternate,
.wcd-day--alternate .wcd-day-body {
  background: var(--day-alternate);
}
.wcd-day:last-child .wcd-day-header {
  border-bottom-left-radius: 11px;
}
.wcd-day-header {
  position: relative;
  min-width: 0;
  padding: 5px 4px 5px 7px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  text-align: center;
  border-right: 1px solid var(--border);
  background: linear-gradient(145deg, rgba(34, 163, 90, 0.14), var(--surface-2));
  box-shadow: inset 3px 0 0 rgba(34, 163, 90, 0.7);
}
.wcd-day--alternate .wcd-day-header {
  background: linear-gradient(145deg, rgba(59, 130, 246, 0.11), var(--surface-2));
  box-shadow: inset 3px 0 0 rgba(59, 130, 246, 0.58);
}
.wcd-day-weekday {
  color: var(--v-primary-base);
  font-size: 0.5rem;
  font-weight: 950;
  line-height: 1;
  letter-spacing: 0.08em;
}
.wcd-day-date {
  margin-top: 1px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 3px;
}
.wcd-day-date strong {
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 950;
  line-height: 1;
}
.wcd-day-date span {
  color: var(--muted);
  font-size: 0.5rem;
  font-weight: 850;
  text-transform: uppercase;
}
.wcd-day-header small {
  max-width: 66px;
  margin-top: 2px;
  padding: 1px 5px;
  overflow: hidden;
  color: #56cb80;
  font-size: 0.45rem;
  font-style: normal;
  font-weight: 850;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: rgba(34, 163, 90, 0.11);
  border-radius: 999px;
}
.wcd-day-header small.empty {
  color: var(--muted);
  background: rgba(148, 163, 184, 0.08);
}
.wcd-day-body {
  min-width: 0;
  padding: 2px 8px;
}
.wcd-timeline-row {
  min-height: 0;
  padding: 2px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--border);
  transition: height 0.18s ease;
}
.wcd-timeline-row:last-child {
  border-bottom: 0;
}
.wcd-employee {
  width: 108px;
  flex: 0 0 108px;
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.wcd-employee .v-avatar {
  border: 1px solid var(--border);
}
.wcd-employee > div {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.wcd-employee strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.65rem;
  color: var(--text);
}
.wcd-employee span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.52rem;
  color: var(--muted);
}
.wcd-timeline {
  position: relative;
  flex: 1;
  min-width: 0;
}
.wcd-track {
  position: relative;
  min-height: 30px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: linear-gradient(180deg, rgba(148, 163, 184, 0.035), rgba(15, 18, 23, 0.1));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
}
.wcd-light .wcd-track {
  background: linear-gradient(180deg, #fbfcfd, #f5f7f9);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
}
.wcd-track::before {
  content: "";
  position: absolute;
  z-index: 0;
  inset: 0;
  background-image: linear-gradient(to right, rgba(148, 163, 184, 0.06) 1px, transparent 1px),
    linear-gradient(to right, rgba(148, 163, 184, 0.16) 1px, transparent 1px);
  background-size: var(--quarter-grid) 100%, var(--hour-grid) 100%;
  pointer-events: none;
}
.wcd-track--off::before {
  opacity: 0.35;
}
.wcd-track-empty {
  position: absolute;
  z-index: 1;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: var(--muted);
  font-size: 0.55rem;
  font-weight: 750;
  pointer-events: none;
  opacity: 0.7;
}

.wcd-free-block {
  position: absolute;
  z-index: 1;
  top: 3px;
  height: calc(100% - 6px);
  min-width: 2px;
  padding: 0 5px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  box-sizing: border-box;
  color: #68d991;
  background: rgba(34, 163, 90, 0.11);
  border: 1px dashed rgba(83, 210, 129, 0.5);
  border-radius: 7px;
  box-shadow: inset 0 0 0 1px rgba(34, 163, 90, 0.035);
  white-space: nowrap;
  font-size: 0.49rem;
  font-weight: 750;
  cursor: pointer;
  transition: background 0.14s ease, border-color 0.14s ease, box-shadow 0.14s ease;
}
.wcd-free-block span {
  overflow: hidden;
  text-overflow: ellipsis;
}
.wcd-free-block span strong {
  font-weight: 950;
}
.wcd-free-block:hover,
.wcd-free-block:focus-visible {
  z-index: 3;
  color: #8df0ad;
  background: rgba(34, 163, 90, 0.22);
  border-color: rgba(113, 233, 155, 0.9);
  box-shadow: 0 0 0 2px rgba(34, 163, 90, 0.12);
  outline: none;
}
.wcd-free-block.short {
  color: #8d98a6;
  border-style: solid;
  border-color: rgba(148, 163, 184, 0.26);
  background: repeating-linear-gradient(135deg, rgba(148, 163, 184, 0.14) 0 4px, rgba(71, 85, 105, 0.08) 4px 8px);
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.62;
}
.wcd-light .wcd-free-block {
  color: #187b43;
  background: rgba(34, 163, 90, 0.075);
  border-color: rgba(26, 145, 76, 0.42);
}
.wcd-light .wcd-free-block:hover,
.wcd-light .wcd-free-block:focus-visible {
  color: #0e6333;
  background: rgba(34, 163, 90, 0.15);
  border-color: rgba(26, 145, 76, 0.72);
}
.wcd-light .wcd-free-block.short {
  color: #667085;
  background: repeating-linear-gradient(135deg, rgba(148, 163, 184, 0.14) 0 4px, rgba(203, 213, 225, 0.09) 4px 8px);
}

.wcd-event-block {
  position: absolute;
  z-index: 4;
  min-width: 5px;
  min-height: 0;
  padding: 0 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  box-sizing: border-box;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.24);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition: filter 0.14s ease, box-shadow 0.14s ease, transform 0.14s ease;
}
.wcd-event-block:hover,
.wcd-event-block:focus-visible {
  z-index: 8;
  filter: brightness(1.12);
  box-shadow: 0 4px 11px rgba(0, 0, 0, 0.34);
  outline: 2px solid rgba(255, 255, 255, 0.82);
  outline-offset: 1px;
}
.wcd-event-block span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.53rem;
  font-weight: 950;
}
.wcd-event-block .wcd-event-short-label {
  flex: 1 1 auto;
  font-size: 0.47rem;
  letter-spacing: -0.02em;
  text-align: center;
}
.wcd-event-block small {
  flex: 0 0 auto;
  margin-left: auto;
  font-size: 0.43rem;
  font-weight: 800;
  opacity: 0.9;
}
.wcd-event-block.dense {
  padding: 0 3px;
  border-radius: 5px;
}
.wcd-event-block.micro {
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}
.wcd-event-block.parallel {
  background-image: repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.13) 0 4px, transparent 4px 8px);
  outline: 1px dashed #ffe5a7;
  outline-offset: -2px;
}
.wcd-event-block.muted {
  opacity: 0.42;
}
.event-absence {
  background-image: repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0 5px, transparent 5px 10px);
}
.wcd-event-tooltip-content {
  min-width: 150px;
  padding: 2px 0;
  display: flex;
  flex-direction: column;
}
.wcd-event-tooltip-content span {
  color: #aab4c2;
  font-size: 0.58rem;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}
.wcd-event-tooltip-content strong {
  margin-top: 1px;
  color: #fff;
  font-size: 0.72rem;
}
.wcd-event-tooltip-content small {
  margin-top: 2px;
  color: #d3d8df;
  font-size: 0.6rem;
}

.wcd-selection {
  position: absolute;
  z-index: 7;
  top: 50%;
  height: 28px;
  min-width: 5px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  transform: translateY(-50%);
  box-sizing: border-box;
  border: 2px solid #83eea7;
  border-radius: 7px;
  background: rgba(34, 197, 94, 0.14);
  pointer-events: none;
  box-shadow: 0 0 0 1px rgba(7, 15, 10, 0.42), 0 0 10px rgba(34, 197, 94, 0.25);
}
.wcd-selection::before {
  content: "";
  position: absolute;
  inset: 2px;
  border-radius: 4px;
  background: rgba(34, 197, 94, 0.12);
}
.wcd-selection span {
  position: relative;
  z-index: 1;
  margin-bottom: 1px;
  padding: 1px 3px;
  border-radius: 3px;
  color: #fff;
  background: rgba(10, 20, 14, 0.82);
  font-size: 0.45rem;
  font-weight: 900;
}
.wcd-selection.conflict {
  border-color: #fbbf24;
  background: rgba(245, 158, 11, 0.14);
  box-shadow: 0 0 0 1px rgba(7, 15, 10, 0.42), 0 0 10px rgba(245, 158, 11, 0.27);
}
.wcd-selection.conflict::before {
  background: rgba(245, 158, 11, 0.14);
}

.wcd-detail-card {
  --detail-color: #64748b;
  position: relative;
  z-index: 9;
  grid-column: 2;
  min-width: 0;
  margin: 0 8px 6px;
  padding: 7px 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  border: 1px solid var(--border);
  border-left: 3px solid var(--detail-color);
  border-radius: 9px;
  background: var(--surface-3);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
}
.wcd-detail-icon {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--detail-color);
}
.wcd-detail-copy {
  min-width: 120px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.wcd-detail-copy > span {
  color: var(--detail-color);
  font-size: 0.52rem;
  font-weight: 900;
  text-transform: uppercase;
}
.wcd-detail-copy strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
  font-size: 0.72rem;
}
.wcd-detail-copy small {
  color: var(--muted);
  font-size: 0.56rem;
}
.wcd-detail-state {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 9px;
  color: #ef4444;
  font-size: 0.54rem;
  font-weight: 850;
  white-space: nowrap;
  border: 1px solid rgba(239, 68, 68, 0.28);
  border-radius: 999px;
  background: rgba(239, 68, 68, 0.08);
}
.wcd-detail-state.warning {
  color: #fbbf24;
  border-color: rgba(245, 158, 11, 0.32);
  background: rgba(245, 158, 11, 0.1);
}
.wcd-light .wcd-detail-state.warning {
  color: #b45309;
  border-color: rgba(180, 83, 9, 0.28);
  background: rgba(245, 158, 11, 0.09);
}
.wcd-detail-action {
  min-height: 30px !important;
  padding: 0 11px !important;
  letter-spacing: 0.01em !important;
  background: rgba(245, 158, 11, 0.06) !important;
}
.wcd-selection-panel {
  position: relative;
  z-index: 20;
  flex: 0 0 auto;
  padding: 13px 42px 13px 15px;
  display: grid;
  grid-template-columns: minmax(170px, 220px) minmax(220px, 1fr) minmax(210px, 270px);
  align-items: center;
  gap: 14px;
  border-top: 2px solid var(--v-primary-base);
  background: var(--surface);
  box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.3);
}
.wcd-close-selection {
  position: absolute;
  right: 9px;
  top: 8px;
  color: var(--muted);
}
.wcd-selected-person {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}
.wcd-selected-person > div {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.wcd-selected-person span,
.wcd-selection-summary span,
.wcd-start-picker label span {
  color: var(--muted);
  font-size: 0.55rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.wcd-selected-person strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
  font-size: 0.78rem;
}
.wcd-selected-person small {
  color: #6bd48f;
  font-size: 0.57rem;
}
.wcd-start-options {
  display: flex;
  gap: 5px;
  overflow-x: auto;
  padding-bottom: 3px;
  scrollbar-width: thin;
}
.wcd-start-option {
  min-width: 57px;
  min-height: 43px;
  padding: 4px;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  align-items: center;
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text);
  background: var(--surface-2);
}
.wcd-start-option strong {
  font-size: 0.67rem;
}
.wcd-start-option span {
  grid-column: 1;
  color: var(--muted);
  font-size: 0.5rem;
}
.wcd-start-option .v-icon {
  grid-column: 2;
  grid-row: 1/3;
}
.wcd-start-option.active {
  border-color: #5cdb89;
  background: rgba(34, 163, 90, 0.16);
}
.wcd-start-option.conflict {
  border-color: rgba(245, 158, 11, 0.5);
}
.wcd-start-option.conflict.active {
  background: rgba(245, 158, 11, 0.14);
}
.wcd-selection-summary {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 9px;
  align-items: center;
}
.wcd-selection-summary > div:first-child {
  display: flex;
  flex-direction: column;
}
.wcd-selection-summary > div:first-child strong {
  color: var(--text);
  font-size: 0.83rem;
}
.wcd-selection-summary > div:first-child small {
  color: var(--muted);
  font-size: 0.56rem;
}
.wcd-selection-summary > .v-btn {
  grid-column: 1/3;
  height: 34px !important;
}
.wcd-active-warning,
.wcd-active-safe {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}
.wcd-active-warning > span {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.wcd-active-warning strong {
  color: #fbbf24;
  font-size: 0.59rem;
}
.wcd-active-warning small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--muted);
  font-size: 0.52rem;
}
.wcd-active-safe {
  color: #62d48a;
  font-size: 0.6rem;
  font-weight: 800;
}
.wcd-dialog-card {
  border-radius: 15px !important;
}
.wcd-dialog-icon {
  width: 38px;
  height: 38px;
  margin-right: 10px;
  flex: 0 0 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  background: #f59e0b;
}
.wcd-dialog-locks {
  margin-top: 13px;
  padding: 9px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  border-radius: 8px;
  background: rgba(34, 163, 90, 0.1);
  font-size: 0.73rem;
}
.wcd-conflict-list {
  margin: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.wcd-conflict-list > div {
  padding: 7px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
}
.wcd-conflict-list > div > span {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wcd-conflict-list > div > div {
  display: flex;
  flex-direction: column;
}
.wcd-conflict-list strong {
  font-size: 0.72rem;
}
.wcd-conflict-list small {
  color: var(--muted);
  font-size: 0.61rem;
}
@media (max-width: 1100px) {
  .wcd-selection-panel {
    grid-template-columns: 180px 1fr;
  }
  .wcd-selection-summary {
    grid-column: 1/3;
    grid-template-columns: auto 1fr auto;
  }
  .wcd-selection-summary > .v-btn {
    grid-column: auto;
  }
  .wcd-weekdays-field {
    width: 42%;
  }
}
@media (max-width: 760px) {
  .wcd-scroll {
    padding: 12px;
  }
  .wcd-control-row {
    display: block;
  }
  .wcd-field-date,
  .wcd-weekdays-field {
    width: 100%;
    margin-top: 10px;
  }
  .wcd-employee-filter-line {
    grid-template-columns: minmax(0, 1fr);
  }
  .wcd-own-employee-switch {
    width: 100%;
  }
  .wcd-time-choices {
    grid-template-columns: repeat(2, 1fr);
  }
  .wcd-weekday {
    min-height: 42px;
  }
  .wcd-schedule-axis,
  .wcd-day {
    grid-template-columns: 60px minmax(0, 1fr);
  }
  .wcd-axis-content {
    padding: 0 6px;
    gap: 6px;
  }
  .wcd-axis-employee,
  .wcd-employee {
    width: 78px;
    flex-basis: 78px;
  }
  .wcd-day-body {
    padding-right: 6px;
    padding-left: 6px;
  }
  .wcd-timeline-row {
    gap: 6px;
  }
  .wcd-employee .v-avatar {
    display: none;
  }
  .wcd-axis-ruler span {
    font-size: 0.43rem;
  }
  .wcd-day-header small {
    max-width: 54px;
    padding-right: 3px;
    padding-left: 3px;
    font-size: 0.43rem;
  }
  .wcd-detail-card {
    grid-column: 1 / -1;
    margin: 0 6px 6px;
    align-items: flex-start;
  }
  .wcd-detail-state {
    margin-left: 37px;
  }
  .wcd-selection-panel {
    grid-template-columns: 1fr;
    padding: 12px 40px 12px 12px;
  }
  .wcd-start-picker,
  .wcd-selection-summary {
    grid-column: 1;
  }
  .wcd-selection-summary {
    grid-template-columns: auto 1fr;
  }
  .wcd-selection-summary > .v-btn {
    grid-column: 1/3;
  }
  .wcd-selected-person .v-avatar {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
  }
}
@media (max-width: 520px) {
  .wcd-schedule-axis,
  .wcd-day {
    grid-template-columns: 54px minmax(0, 1fr);
  }
  .wcd-axis-employee,
  .wcd-employee {
    width: 64px;
    flex-basis: 64px;
  }
  .wcd-employee span {
    display: none;
  }
  .wcd-axis-ruler span {
    font-size: 0.4rem;
  }
}
@media (pointer: coarse) {
  button {
    touch-action: manipulation;
  }
  .wcd-weekday,
  .wcd-choice,
  .wcd-date-button {
    min-height: 44px;
  }
  .wcd-free-block {
    height: calc(100% - 6px);
  }
  .wcd-event-block {
    min-width: 5px;
    overflow: visible;
  }
  .wcd-event-block::before {
    position: absolute;
    top: 50%;
    right: 0;
    left: 0;
    height: 36px;
    content: "";
    transform: translateY(-50%);
  }
  .wcd-start-option {
    min-height: 48px;
    min-width: 62px;
  }
}
</style>
